import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface ErrorBoundaryProps {
  children: ReactNode;
  title?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('React error boundary caught an error', error, errorInfo);
  }

  public override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <Card className="mx-auto flex max-w-md flex-col items-center gap-3 px-6 py-8 text-center">
        <AlertCircle className="h-7 w-7 text-destructive" aria-hidden />
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {this.props.title ?? 'Something went wrong.'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Reload the app to get back to a clean state.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => window.location.reload()}>
          <RotateCw className="h-3.5 w-3.5" aria-hidden />
          Reload
        </Button>
      </Card>
    );
  }
}
